import { CircularProgress, Grid, TableRow, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import Location from "../../../../api/Admin/Location";
import UnitOfMeasure from "../../../../api/Admin/UnitOfMeasure";
import ContainerStatus from "../../../../api/LIMS/ContainerStatus";
import ContainerType from "../../../../api/LIMS/ContainerType";
import WorkRequest from "../../../../api/WorkRequest/WorkRequest";
import ModalMessages from "../../../../components/Modal/ModalSimpleButton";
import LoadingButton from "@mui/lab/LoadingButton";
import EmailAddressTextField from "../../../../components/EmailAddressField";
import {
  StyledTableBody,
  StyledTableCell,
  UXDataTableWithoutBody,
} from "../../../../components/UXDataTable";
import { GlobalButton, Option, StyledAutocomplete } from "../../../styles";
import { ConvertUOMs, RecalculateCurrentAmountOnUoMChange, RecalculateInitialSizeOnUoMChange, GetRequiredSampleSize, GetRequiredSampleSizeUoM } from "../../../../global";
import { newContainerErrorChecks } from "../constants";
import PrintInformation from "../../../../components/PrintInformation";
import PrintLabel from "../../../../api/LIMS/PrintLabel";

const containerGridFontSize = 12;
const newContainersCol = [
  "Midas #",
  "Container #",
  "Parent Container",
  "Type",
  "Status",
  "Initial Size",
  "Current Amt",
  "UoM",
  "% Full",
  "Location",
  "Sublocation",
  "Owner",
];

export default function ManageContainerWithoutTests({
  workRequest,
  prepareData,
  mySamplesInfo,
  setMySamplesInfo,
  isReadOnly,
  isShipping,
}) {
  const [loading, setLoading] = useState(false);
  const [containerTypes, setContainerTypes] = useState([]);
  const [containerStatuses, setContainerStatuses] = useState([]);
  const [availableUOMs, setAvailableUOMs] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [containerErrorChecks, setContainerErrorChecks] = useState(
    mySamplesInfo.map((x, i) => ({
      ...newContainerErrorChecks,
    }))
  );

  const [modalMessagesOpen, setModalMessagesOpen] = useState(false);
  const modalMessagesButtonText = 'Ok'
  const [modalMessagesTitle, setModalMessagesTitle] = useState('');
  const [modalMessagesText, setModalMessagesText] = useState('');
  const [printLabelInfo, setPrintLabelInfo] = useState(null);
  const [printInfoOpen, setPrintInfoOpen] = useState(false);
  const [disablePrintContainers, setDisablePrintContainers] = useState(true);

  const inputErrorAndDisabledStyle = {
    '& .MuiOutlinedInput-root.Mui-disabled.Mui-error': {
      '& fieldset': { borderColor: 'red' }
    }
  };

  useEffect(() => {
    const hasErrors = containerErrorChecks.some((containerError) => {
      return Object.keys(containerError).some((key) => {
        if (containerError[key]) return true;
        return false;
      });
    }) ?? false;

    setHasError(hasErrors);
  }, [containerErrorChecks]);

  useEffect(() => {
    let cancelPromise = false;

    Location.getAll().then((res) => {
      if (cancelPromise) return;
      setAvailableLocations(
        res
          .filter((result) => result.isActive === true)
          .sort((a, b) => a.locationName.localeCompare(b.locationName))
      );
    });

    UnitOfMeasure.getAll().then((res) => {
      if (cancelPromise) return;
      setAvailableUOMs(
        res
          .filter(
            (result) =>
              (result.metricStandardConversion !== null ||
                result.type === "concentration") &&
              result.isActive === true
          )
          .sort((a, b) => a.uoMName.localeCompare(b.uoMName))
      );
    });

    ContainerStatus.getAll().then((res) => {
      if (cancelPromise) return;
      setContainerStatuses(
        res
          .filter((result) => result.isActive === true)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    });

    ContainerType.getAll().then((res) => {
      if (cancelPromise) return;
      setContainerTypes(
        res
          .filter((result) => result.isActive === true)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    });

    return () => {
      cancelPromise = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMySamplesInfo((prev) => [
      ...prev.map((x) => {
        return {
          ...x,
          newContainer: x.newContainer ?? null,
        };
      }),
    ]);
  }, [setMySamplesInfo]);

  // Sync containerErrorChecks array size with mySamplesInfo
  useEffect(() => {
    if (containerErrorChecks.length !== mySamplesInfo.length) {
      const newErrorChecks = mySamplesInfo.map((sample, index) => {
        // Keep existing error check if available, otherwise create new one
        return containerErrorChecks[index] || { ...newContainerErrorChecks };
      });
      setContainerErrorChecks(newErrorChecks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySamplesInfo.length]);

  // Validate all containers when mySamplesInfo or availableUOMs change
  useEffect(() => {
    if (!availableUOMs.length) return; // Wait for UOMs to load

    const newErrorChecks = mySamplesInfo.map((sample, index) => {
      const newContainer = sample?.newContainer;
      if (!newContainer) {
        return { ...newContainerErrorChecks };
      }
      return validateContainer(sample, newContainer);
    });

    setContainerErrorChecks(newErrorChecks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySamplesInfo, availableUOMs]);


  function closeModalMessages() {
    setModalMessagesOpen(false);
  }

  function openModalMessages(title, text) {
    setModalMessagesOpen(true);
    setModalMessagesTitle(title);
    setModalMessagesText(text);
  }

  async function handleGetTestContainers() {
    let types = containerTypes;
    if (containerTypes.length === 0) {
      types = await ContainerType.getAll();
      types = types
        .filter((result) => result.isActive === true)
        .sort((a, b) => a.name.localeCompare(b.name));
      setContainerTypes(types);
    }

    let newSampleList = [];
    const defaultLocation = isShipping ? 'XSHIP' : null;
    
    mySamplesInfo.forEach((sample) => {
      const currentAmount = sample.newContainer?.currentAmount ?? sample.requestedAmount ?? 0;
      const type = [...types].sort((a, b) => a.defaultContainerSize - b.defaultContainerSize).find(f => f.defaultContainerSize > currentAmount && f.defaultContainerUoMName === sample.requestedAmountUoM);
      const size = sample.newContainer?.size ?? type?.defaultContainerSize ?? 0;

      newSampleList.push({
        ...sample,
        sampleName: sample?.sampleName ?? sample?.sample?.sampleName,
        newContainer: {
          containerStatusName: "Confirmed",
          locationName: sample.newContainer?.locationName ?? defaultLocation,
          parentContainer:
            sample.newContainer?.parentContainer ??
            sample.existingContainer ??
            null,
          containerNumber: sample.newContainer?.containerNumber ?? 0,
          containerType: type ?? sample.newContainer?.containerType,
          containerTypeName: type?.name ?? sample.newContainer?.containerTypeName,
          createdDate: sample.newContainer?.createdDate ?? null,
          ownerEmail:
            sample.newContainer?.ownerEmail ??
            workRequest.requestedByEmail,
          subLocation: sample.newContainer?.subLocation ?? null,
          size: size,
          containerSizeUoM: sample.newContainer?.containerSizeUoM ?? type?.defaultContainerUoMName ?? sample.requestedAmountUoM,
          currentAmount: currentAmount,
          returnLocationName: sample?.requestedLocationName
        },
      });
    });

    setMySamplesInfo(newSampleList);
  }

  const isContainersAlreadyFetched = (sampleList) => {
    return sampleList.some((x) => x.newContainer?.containerNumber > 0);
  };

  const handleContainerRowChange = (property, value, index) => {
    let newSampleList = structuredClone(mySamplesInfo);
    let sample = newSampleList[index];
    let container = sample.newContainer;
    if (property === "containerSizeUoM") {
      if (value) {
        const targetUomObj = availableUOMs.find((x) => x.uoMName === value);
        const currentUomObj = availableUOMs.find(
          (x) => x.uoMName === container.containerSizeUoM
        );
        container.currentAmount = RecalculateCurrentAmountOnUoMChange(
          sample.sample,
          sample.sample.substance,
          container.currentAmount,
          currentUomObj,
          targetUomObj
        );
        container.size = RecalculateInitialSizeOnUoMChange(
          sample.sample,
          sample.sample.substance,
          container.size,
          currentUomObj,
          targetUomObj
        );
      }
    } else if (property === "containerTypeName") {
      const type = containerTypes.find(f => f.name === value);
      container['containerType'] = type;
      container['size'] = type?.defaultContainerSize ?? container['size'];
    }

    container[property] = value;
    setMySamplesInfo(newSampleList);
    checkPropertyErrors(sample, index, container);
  };

  const validateContainer = (sample, newContainer) => {
    let containerError = structuredClone(newContainerErrorChecks);
    
    // Validate containerTypeName
    containerError.containerTypeName = !newContainer.containerTypeName;
    
    // Validate containerStatusName
    containerError.containerStatusName = !newContainer.containerStatusName;
    
    // Validate size
    containerError.size =
      !newContainer.size ||
      isNaN(newContainer.size) ||
      newContainer.size < newContainer.currentAmount;
    
    // Validate currentAmount
    let minimumReq = newContainer.tests?.reduce(
      (acc, value) =>
        acc +
        ConvertUOMs(
          sample.sample,
          sample.sample.substance,
          GetRequiredSampleSize(value),
          availableUOMs.find(
            (x) => x.uoMName === GetRequiredSampleSizeUoM(value)
          ),
          availableUOMs.find(
            (x) => x.uoMName === newContainer.containerSizeUoM
          )
        ),
      0
    ) || 0;
    
    containerError.currentAmount =
      !newContainer.currentAmount ||
      isNaN(newContainer.currentAmount) ||
      newContainer.currentAmount > newContainer.size ||
      newContainer.currentAmount < minimumReq;
    
    // Validate containerSizeUoM
    containerError.containerSizeUoM = !newContainer.containerSizeUoM;
    
    // Validate locationName
    containerError.locationName = !newContainer.locationName;

    return containerError;
  };

  const checkPropertyErrors = (sample, currentLine, newContainer) => {
    const containerError = validateContainer(sample, newContainer);
    let newErrorCheck = structuredClone(containerErrorChecks);
    newErrorCheck[currentLine] = { ...containerError };
    setContainerErrorChecks(newErrorCheck);
  };

  const handleCreateContainers = async () => {
    setLoading(true);

    let updatedWorkRequest = structuredClone(workRequest);
    updatedWorkRequest.workRequestSamples = mySamplesInfo;

    const [data] = await prepareData(updatedWorkRequest, false, true);

    WorkRequest.createContainers(data)
      .then((res) => {
        setLoading(false);

        if (res && res.result) {
          // let updatedSamples = structuredClone(mySamplesInfo);
          // updatedSamples.forEach((sample) => {
          //   let currentNewContainer = res.result.workRequestSamples.find(
          //     (x) => x.id === sample.id
          //   ).newContainer;
          //   if (currentNewContainer && sample.newContainer) {
          //     sample.newContainer.containerNumber =
          //     currentNewContainer.containerNumber;
          //     sample.newContainer.createdDate = currentNewContainer.createdDate;
          //   }
          // });
          // setMySamplesInfo(updatedSamples);
          const newSamplesInfo = res.result.workRequestSamples.map((returnedSample) => {
            const oldSample = mySamplesInfo.find(
              s =>
                (s.existingContainerID === returnedSample.existingContainerID)
            );
            return {
              ...returnedSample,
              sample: oldSample?.sample ?? returnedSample.sample,
            };
          })
          setMySamplesInfo(newSamplesInfo);
          openModalMessages("Creating Containers", `Created containers with success`);
          setDisablePrintContainers(false);
        } else {
          openModalMessages("Error Creating Containers", `There was an error creating containers. ${res.message ? res.message : ''}`)
        }
      })
  };

  function openPrintingOptions(sampleList, onlyWithBlindCode) {
    let arrayPrintInfo = [];
    if (sampleList?.length > 0) {
      sampleList.forEach((dataTable) => {
        if (dataTable?.sample && dataTable?.newContainer) {
          const containerNumbers = [`${dataTable.newContainer?.containerNumber}`];
          let newPrintInfo = new PrintLabel({
            sampleName: dataTable.sample.sampleName,
            containerNumbers: containerNumbers,
            includeAdditionalSampleInformation: false,
            chemIDOnly: false,
            printLabelSizeType: null,
            isBlindCoded: onlyWithBlindCode,
            blindDescription: null,
            blindSampleName: null,
            isShelfLabel: false,
            shelfLabelText: null,
            includeShelfBarcode: false
          });
          arrayPrintInfo.push(newPrintInfo);
        }
      });
      setPrintLabelInfo(arrayPrintInfo);
      setPrintInfoOpen(true);
    } else {
      openModalMessages('', 'A container needs to be created to be able to print!');
    }
  }

  function closePrintInformation() {
    setPrintInfoOpen(false);
  }

  return (
    <Grid container>
      <LoadingButton
        variant="outlined"
        onClick={handleGetTestContainers}
        disabled={isContainersAlreadyFetched(mySamplesInfo) || isReadOnly || workRequest?.status === 'Completed'}
        style={{ textTransform: "initial" }}
        loading={false}
      >
        Get Containers
      </LoadingButton>

      <UXDataTableWithoutBody
        tableWidth={"100%"}
        cols={newContainersCol}
        blankFirstHeader={false}
        tableId="containerTable"
        rowLength={mySamplesInfo.length}
        enablePaging={false}
        rowsPerPage={0}
        page={0}
        handleChangePage={null}
        handleChangeRowsPerPage={null}
        noDataFoundMessage={"No containers found"}
        enableAddIcon={false}
        addFunction={false}
        addToolTipText={"Add Container"}
        isDataLoading={false}
      >
        <StyledTableBody>
          {mySamplesInfo.map((sample, index) => {
            let container = sample.newContainer;
            const parentContainer = container?.parentContainer || sample?.sample?.containers?.find(c => c.id === container?.parentID) || null;
            if (!container || !availableUOMs.length) return <></>;

            let minimumReq = container.tests?.reduce(
              (acc, value) =>
                acc +
                ConvertUOMs(
                  sample.sample,
                  sample.sample.substance,
                  GetRequiredSampleSize(value),
                  availableUOMs.find(
                    (x) =>
                      x.uoMName === GetRequiredSampleSizeUoM(value)
                  ),
                  availableUOMs.find(
                    (x) => x.uoMName === container.containerSizeUoM
                  )
                ),
              0
            );

            return (
              <TableRow key={`ContainerRow${index}`}>
                <StyledTableCell style={{ width: "15px" }}>
                  <b>{sample.sampleName}</b>
                </StyledTableCell>

                <StyledTableCell style={{ width: "15px" }}>
                  <b>{container.containerNumber}</b>
                </StyledTableCell>

                <StyledTableCell>
                  <StyledAutocomplete
                    renderOption={(props2, option) => (
                      <Option {...props2}>{option.containerNumber}</Option>
                    )}
                    disabled={!!container.createdDate || isReadOnly}
                    disablePortal
                    noOptionsText={"No containers found"}
                    options={sample?.sample?.containers ? sample.sample.containers
                      .sort((a, b) => a.containerNumber - b.containerNumber)
                      .filter((c) => c.containerStatusName !== "Discarded") : []}
                    getOptionLabel={(option) =>
                      option.containerNumber.toString()
                    }
                    onChange={(e, value) =>
                      handleContainerRowChange("parentContainer", value, index)
                    }
                    autoHighlight
                    autoSelect
                    value={isReadOnly ? parentContainer : container.parentContainer}
                    isOptionEqualToValue={(option, value) =>
                      value.containerNumber === option.containerNumber
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        style={{ width: "100px" }}
                        variant="outlined"
                        size="small"
                        inputProps={{
                          ...params.inputProps,
                          style: { fontSize: containerGridFontSize },
                        }}
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <StyledAutocomplete
                    renderOption={(props2, option) => (
                      <Option {...props2}>{option.name}</Option>
                    )}
                    disabled={!!container.createdDate || isReadOnly}
                    disablePortal
                    autoHighlight
                    autoSelect
                    noOptionsText={"Loading Types..."}
                    options={containerTypes}
                    onChange={(e, value) => {
                      handleContainerRowChange(
                        "containerTypeName",
                        value?.name,
                        index
                      );
                    }}
                    getOptionLabel={(option) => option.name ?? option}
                    value={container.containerTypeName}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        style={{ width: "138px" }}
                        sx={inputErrorAndDisabledStyle}
                        variant="outlined"
                        size="small"
                        error={containerErrorChecks[index]?.containerTypeName}
                        inputProps={{
                          ...params.inputProps,
                          style: { fontSize: containerGridFontSize },
                        }}
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <StyledAutocomplete
                    renderOption={(props2, option) => (
                      <Option {...props2}>{option.name}</Option>
                    )}
                    getOptionLabel={(option) => option.name ?? option}
                    disabled={true}
                    disablePortal
                    noOptionsText={"Loading Statuses..."}
                    options={containerStatuses}
                    onChange={(e, value) =>
                      handleContainerRowChange(
                        "containerStatusName",
                        value?.name,
                        index
                      )
                    }
                    value={container.containerStatusName}
                    autoHighlight
                    autoSelect
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        style={{ width: "145px" }}
                        sx={inputErrorAndDisabledStyle}
                        variant="outlined"
                        size="small"
                        error={containerErrorChecks[index]?.containerStatusName}
                        inputProps={{
                          ...params.inputProps,
                          style: { fontSize: containerGridFontSize },
                        }}
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <TextField
                    style={{ width: "60px", marginTop: "5px" }}
                    sx={inputErrorAndDisabledStyle}
                    disabled={!!container.createdDate || isReadOnly}
                    size="small"
                    margin="dense"
                    variant="outlined"
                    inputProps={{
                      style: { fontSize: containerGridFontSize },
                    }}
                    InputLabelProps={{ shrink: true }}
                    value={container.size}
                    error={containerErrorChecks[index]?.size}
                    onChange={(e) =>
                      handleContainerRowChange("size", e.target.value, index)
                    }
                    onBlur={(e) => { }}
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <TextField
                    style={{ width: "60px", marginTop: "5px" }}
                    sx={inputErrorAndDisabledStyle}
                    disabled={!!container.createdDate || isReadOnly}
                    size="small"
                    margin="dense"
                    variant="outlined"
                    inputProps={{
                      style: { fontSize: containerGridFontSize },
                    }}
                    InputLabelProps={{ shrink: true }}
                    value={container.currentAmount}
                    error={
                      containerErrorChecks[index]?.currentAmount ||
                      container.currentAmount < minimumReq
                    }
                    onChange={(e) =>
                      handleContainerRowChange(
                        "currentAmount",
                        e.target.value,
                        index
                      )
                    }
                    onBlur={(e) => { }}
                  />
                </StyledTableCell>

                {/* UoM */}
                <StyledTableCell>
                  <StyledAutocomplete
                    renderOption={(props2, option) => (
                      <Option {...props2}>{option.uoMName}</Option>
                    )}
                    disabled={!!container.createdDate || isReadOnly}
                    disablePortal
                    noOptionsText={"Loading UOMs..."}
                    options={availableUOMs.filter(
                      (result) =>
                        result.type === "weight" ||
                        result.type === "volume" ||
                        result.type === "unit"
                    )}
                    getOptionLabel={(option) => option.uoMName ?? option}
                    onChange={(e, value) => {
                      handleContainerRowChange(
                        "containerSizeUoM",
                        value?.uoMName,
                        index
                      );
                    }}
                    value={container.containerSizeUoM}
                    autoHighlight
                    autoSelect
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        style={{ width: "80px" }}
                        sx={inputErrorAndDisabledStyle}
                        variant="outlined"
                        size="small"
                        error={containerErrorChecks[index]?.containerSizeUoM}
                        inputProps={{
                          ...params.inputProps,
                          style: { fontSize: containerGridFontSize },
                        }}
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                  />
                </StyledTableCell>

                <StyledTableCell>
                  {container.size > 0
                    ? (
                      (container.currentAmount / container.size) *
                      100
                    ).toFixed(2)
                    : 0}
                  %
                </StyledTableCell>

                <StyledTableCell>
                  <StyledAutocomplete
                    disabled={isReadOnly}
                    value={container.locationName}
                    onChange={(e, value) =>
                      handleContainerRowChange(
                        "locationName",
                        value?.locationName,
                        index
                      )
                    }
                    disablePortal
                    autoHighlight
                    autoSelect
                    options={availableLocations}
                    renderOption={(props2, option) => (
                      <Option {...props2}>{option.locationName}</Option>
                    )}
                    getOptionLabel={(option) => option.locationName ?? option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        style={{ width: "140px" }}
                        variant="outlined"
                        size="small"
                        error={containerErrorChecks[index]?.locationName}
                        inputProps={{
                          ...params.inputProps,
                          style: { fontSize: containerGridFontSize },
                        }}
                        InputProps={{ ...params.InputProps }}
                      />
                    )}
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <TextField
                    disabled={isReadOnly}
                    style={{ width: "150px", marginTop: "5px" }}
                    size="small"
                    margin="dense"
                    variant="outlined"
                    InputProps={{
                      style: { fontSize: containerGridFontSize },
                      maxLength: 4000,
                    }}
                    InputLabelProps={{ shrink: true }}
                    value={container.subLocation ?? ""}
                    onChange={(e) =>
                      handleContainerRowChange(
                        "subLocation",
                        e.target.value,
                        index
                      )
                    }
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <EmailAddressTextField
                    fontSize={12}
                    fieldWidth={"225px"}
                    validatedUserEmail={container.ownerEmail}
                    setValidatedUserEmail={(e) =>
                      handleContainerRowChange("ownerEmail", e, index)
                    }
                    hasErrors={
                      containerErrorChecks[index]?.ownerEmail ||
                      !container.ownerEmail
                    }
                    setHasErrors={(e) => { }}
                    isDisabled={!!container.createdDate || isReadOnly}
                    labelText="Owner Email"
                    placeholderText="Owner Email"
                    showPlusMeButton={true}
                  />
                </StyledTableCell>
              </TableRow>
            );
          })}
        </StyledTableBody>
      </UXDataTableWithoutBody>
      <GlobalButton
        disabled={
          hasError ||
          isContainersAlreadyFetched(mySamplesInfo) ||
          mySamplesInfo.some((x) => !x.newContainer) ||
          loading ||
          isReadOnly
        }
        variant="contained"
        onClick={handleCreateContainers}
      >
        Create Containers
        {loading ? (
          <CircularProgress
            sx={{ marginLeft: "10px", color: "white" }}
            size={20}
          />
        ) : (
          ""
        )}
      </GlobalButton>
      <GlobalButton
        style={{ width: 160, marginLeft: "10px" }}
        name="printLabels"
        variant="contained"
        disabled={disablePrintContainers && mySamplesInfo.every(s => !s.newContainerID)}
        onClick={() => {
          openPrintingOptions(mySamplesInfo);
        }}
      >
        Print labels
      </GlobalButton>

      {/* Informational Messages */}
      <ModalMessages title={modalMessagesTitle} buttonText={modalMessagesButtonText} buttonAction={closeModalMessages} open={modalMessagesOpen} setOpen={setModalMessagesOpen}>
        <label>
          {modalMessagesText}
        </label>
      </ModalMessages>
      <PrintInformation
        open={printInfoOpen}
        setOpen={setPrintInfoOpen}
        button2Action={closePrintInformation}
        button2Text={"Close"}
        printLabelInfo={printLabelInfo}
        isShelfLabel={false}
        useFieldsBlindCode={false}
      ></PrintInformation>
    </Grid>
  );
}
